import PropTypes from "prop-types";
import React, { useState, useRef } from "react";
import Info from "./Info";
import Notification from "./Notification";
import QuotaLimit from "./quota/QuotaLimit";
import CollectionSelect from "./CollectionSelect";
import {
  Box,
  Flex,
  Text,
  Tooltip,
  Textarea,
  TextInput,
  List,
} from "@mantine/core";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { useStylesGeneral } from "../utils/mantine/stampTabContent/useStylesGeneral";
import CustomButton from "./mantineCustomComponents/CustomButton";
import {
  validatePortfolioContent,
  normalizePortfolioContent,
  getPortfolioTooltipText,
} from "../utils/portfolioValidation";
import ValidationError from "./ValidationError";

const StampFromPortfolio = ({
  currentScreen,
  staticBasePath,
  portfolioText,
  setPortfolioText,
  handlePortfolioToFile,
  isSmallScreen,
  isAuthenticated,
  button,
  buttonStampClassName,
  noQuotaAvailable,
  helpIsActive,
  onSelectedOptionCollection,
  selectedOptionCollection,
  activateSelectCollection,
  onActivateSelectCollection,
  selectCollections,
  portfolioValidation,
  setPortfolioValidation,
}) => {
  // css classes
  const { classes: GeneralClasses } = useStylesGeneral();

  // Portfolio validation state
  const [validationMessage, setValidationMessage] = useState("");
  // Normalized portfolio preview state
  const [normalizedPreview, setNormalizedPreview] = useState("");
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  // File button ref
  const fileInputRef = useRef(null);

  // Validate portfolio CSV format using shared utility
  const validatePortfolio = (text) => {
    if (!text.trim()) {
      setValidationMessage("");
      setPortfolioValidation({ isValid: false, message: "" });
      setNormalizedPreview("");
      return;
    }

    const validation = validatePortfolioContent(text);
    setValidationMessage(validation.message);
    setPortfolioValidation(validation);

    // Generate normalized preview if valid
    if (validation.isValid) {
      const normalized = normalizePortfolioContent(text);
      setNormalizedPreview(normalized);
    } else {
      setNormalizedPreview("");
    }
  };

  // Handle text change
  const handleTextChange = (event) => {
    const value = event.target.value;
    setPortfolioText(value); // Update parent state first (calls handleSetPortfolioText)
    validatePortfolio(value); // Then validate locally for immediate UI feedback
  };

  // Handle file upload (from button or drag-and-drop)
  const handleFileRead = (file) => {
    if (!file) return;

    // Check file type (extension and MIME type)
    const validExtensions = [".csv"];
    const validMimeTypes = ["text/csv", "application/csv"];
    const isValidType =
      validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)) ||
      validMimeTypes.includes(file.type);

    if (!isValidType) {
      setValidationMessage("Please upload a CSV file (.csv)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setPortfolioText(content);
      validatePortfolio(content);
    };
    reader.onerror = () => {
      setValidationMessage("Error reading file. Please try again.");
    };
    reader.readAsText(file);
  };

  // Handle drag events
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (!isAuthenticated) return;

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileRead(files[0]);
    }
  };

  // Get tooltip text using shared utility
  const tooltipText = getPortfolioTooltipText(
    selectedOptionCollection,
    activateSelectCollection,
  );

  return (
    <>
      <Box
        id="verify-method-portfolio__screen"
        className={`${GeneralClasses.wrap} stamp-portfolio-wrap`}
      >
        <Box className={GeneralClasses.wrapContent}>
          {currentScreen === "found" && (
            <Info
              textInfo="You have already stamped this portfolio previously. You can choose another portfolio or stamp this content ID again."
              staticBasePath={staticBasePath}
            />
          )}
          <Box
            className={`${GeneralClasses.stampFromHashContainer} ${GeneralClasses.stampFromPortfolioContainer}`}
          >
            {!isAuthenticated && <Notification />}
            {noQuotaAvailable && <QuotaLimit />}

            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`portfolio-dropzone ${
                isDragging ? "portfolio-dropzone-active" : ""
              }`}
            >
              <Box className="portfolio-textarea-wrapper">
                <Textarea
                  value={portfolioText}
                  onChange={handleTextChange}
                  disabled={!isAuthenticated}
                  minRows={4}
                  aria-label="Input your portfolio as two fields per line: <symbol><delimiter><weight>. Use one delimiter type throughout. Header optional. Default is 'symbol,weight'. You can also drag & drop a portfolio CSV here."
                  styles={() => ({
                    root: {
                      zIndex: !isAuthenticated ? "-1" : "",
                      position: !isAuthenticated ? "relative" : "",
                    },
                  })}
                />
                {!portfolioText && (
                  <Box className="portfolio-placeholder">
                    <Text
                      component="strong"
                      className="portfolio-placeholder-text portfolio-placeholder-bold"
                    >
                      Enter your portfolio in the textbox (or drag & drop /{" "}
                      <CustomAnchor
                        href="#"
                        text="upload a CSV"
                        componentClasses="link"
                        onClick={(e) => {
                          e.preventDefault();
                          if (isAuthenticated && !noQuotaAvailable) {
                            fileInputRef.current?.click();
                          }
                        }}
                      />
                      )
                    </Text>
                    <List>
                      <List.Item>
                        <Text className="portfolio-placeholder-text">
                          Two fields per line:
                          &lt;symbol&gt;&lt;delimiter&gt;&lt;weight&gt;. For
                          example: &quot;AAPL,0.5&quot; or &quot;AAPL;0.5&quot;.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text className="portfolio-placeholder-text">
                          Choose one delimiter (comma, semicolon, or tab) and
                          use it consistently.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text className="portfolio-placeholder-text">
                          Header, such as &quot;symbol,weight&quot; is optional.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text className="portfolio-placeholder-text">
                          Negative weights represent short positions.
                        </Text>
                      </List.Item>
                    </List>
                    <Text
                      component="strong"
                      className="portfolio-placeholder-text portfolio-placeholder-bold"
                    >
                      Example:
                    </Text>

                    <Text className="portfolio-placeholder-text">
                      symbol,weight
                    </Text>

                    <Text className="portfolio-placeholder-text">AAPL,0.5</Text>

                    <Text className="portfolio-placeholder-text">
                      NVDA,-0.2
                    </Text>
                  </Box>
                )}
              </Box>
              {isDragging && isAuthenticated && (
                <Box className="portfolio-dropzone-overlay">
                  <Text className="portfolio-dropzone-overlay-text">
                    Drop CSV file here
                  </Text>
                </Box>
              )}
            </Box>

            <ValidationError message={validationMessage} />

            {portfolioText && (
              <>
                {portfolioValidation.isValid ? (
                  <Flex className="portfolio-status-valid">
                    <Text>✅ Valid Portfolio Structure</Text>
                  </Flex>
                ) : (
                  portfolioValidation.message && (
                    <Flex className="portfolio-status-invalid">
                      <Text>❌ Portfolio Validation Error</Text>
                    </Flex>
                  )
                )}
              </>
            )}

            <Box className="portfolio-upload-section">
              <Text className="portfolio-upload-hint">
                Prefer a file? Drag & drop in the text box, or{" "}
                <CustomAnchor
                  href="#"
                  text="upload a CSV"
                  componentClasses="link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isAuthenticated && !noQuotaAvailable) {
                      fileInputRef.current?.click();
                    }
                  }}
                />
              </Text>
            </Box>

            <TextInput
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              className="portfolio-hidden-input"
              aria-label="Upload portfolio CSV file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  handleFileRead(file);
                }
              }}
            />

            {normalizedPreview && (
              <Box className="portfolio-preview-wrap">
                <Text>Portfolio Preview</Text>
                <Box component="pre" className="portfolio-preview">
                  {normalizedPreview}
                </Box>
                <Text className="portfolio-normalized-note">
                  Portfolio fingerprint (Content ID) is stamped. Portfolio CSV
                  available via confirmation screen and{" "}
                  <CustomAnchor
                    text="Stamped File Storage"
                    href="/profile/#user_data_storage"
                    componentClasses="link"
                  />
                  .
                </Text>
              </Box>
            )}

            {isSmallScreen && (
              <Box className={GeneralClasses.stampInfo}>
                <Tooltip
                  label={tooltipText}
                  disabled={!tooltipText}
                  multiline
                  w={300}
                  withArrow
                  position="top"
                  transitionProps={{ transition: "fade", duration: 200 }}
                >
                  <Box component="span">
                    <CustomButton
                      text="Make a vBase Stamp!"
                      className={buttonStampClassName}
                      onClick={() => handlePortfolioToFile()}
                      disabled={
                        !button ||
                        !portfolioText ||
                        !portfolioValidation.isValid ||
                        (!selectedOptionCollection &&
                          activateSelectCollection === true)
                      }
                      componentClasses={buttonStampClassName}
                    />
                  </Box>
                </Tooltip>

                {!isAuthenticated && (
                  <Box className={GeneralClasses.stampHashMobileLogin}>
                    <CustomAnchor
                      text="Sign In"
                      href="/accounts/login/"
                      componentClasses="link"
                    />
                    <Text component="span"> to Start Stamping!</Text>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={GeneralClasses.stampFromHashContainer}>
        {selectCollections && isAuthenticated && (
          <CollectionSelect
            activateSelectCollection={activateSelectCollection}
            onActivateSelectCollection={onActivateSelectCollection}
            selectCollections={selectCollections}
            onSelectedOptionCollection={onSelectedOptionCollection}
            selectedOptionCollection={selectedOptionCollection}
            helpIsActive={helpIsActive}
            disableCheckbox={true}
            isPortfolioTab={true}
          />
        )}
      </Box>
    </>
  );
};

StampFromPortfolio.propTypes = {
  portfolioText: PropTypes.string.isRequired,
  setPortfolioText: PropTypes.func.isRequired,
  currentScreen: PropTypes.string.isRequired,
  staticBasePath: PropTypes.string.isRequired,
  handlePortfolioToFile: PropTypes.func.isRequired,
  isSmallScreen: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  button: PropTypes.bool.isRequired,
  buttonStampClassName: PropTypes.string.isRequired,
  noQuotaAvailable: PropTypes.bool.isRequired,
  onSelectedOptionCollection: PropTypes.func.isRequired,
  selectedOptionCollection: PropTypes.object,
  activateSelectCollection: PropTypes.bool.isRequired,
  onActivateSelectCollection: PropTypes.func.isRequired,
  helpIsActive: PropTypes.bool.isRequired,
  selectCollections: PropTypes.array,
  portfolioValidation: PropTypes.object.isRequired,
  setPortfolioValidation: PropTypes.func.isRequired,
};

export default StampFromPortfolio;
